import type { Rng } from "./seed";

// A string drawn from the regular expression a schema constrains it with. Without this a property under
// `pattern: "^[A-Z]{3}-\\d{4}$"` gets a sentence of lorem, and the batch fails the very schema it was generated
// from — which is the one promise this page makes. The grammar covered is the one people actually write patterns
// in: literals, classes, groups, alternation and the counted quantifiers. Anything else — a backreference, a
// lookaround, a boundary — is answered with null rather than with a guess, and the page says the pattern was left
// unmet rather than quietly handing back a string that does not match.
export function stringFromPattern(rng: Rng, source: string): string | null {
  try {
    const reader = new Reader(source);
    const node = reader.readAlternation();
    if (!reader.done) return null;
    const value = render(rng, node);
    // The parser is the second opinion on itself: whatever comes out is held to the pattern that produced it, so a
    // corner this grammar reads differently to the engine ends as null rather than as a value that does not match.
    return new RegExp(`^(?:${source})$`).test(value) ? value : null;
  } catch {
    return null;
  }
}

type Node =
  | { type: "alt"; options: Node[] }
  | { type: "seq"; items: Node[] }
  | { type: "repeat"; node: Node; min: number; max: number }
  | { type: "chars"; set: string }
  | { type: "literal"; text: string };

class Reader {
  private at = 0;

  constructor(private readonly source: string) {}

  get done(): boolean {
    return this.at >= this.source.length;
  }

  readAlternation(): Node {
    const options: Node[] = [this.readSequence()];
    while (this.peek() === "|") {
      this.at++;
      options.push(this.readSequence());
    }
    return options.length === 1 ? options[0] : { type: "alt", options };
  }

  private readSequence(): Node {
    const items: Node[] = [];
    while (!this.done && this.peek() !== "|" && this.peek() !== ")") items.push(this.readQuantified());
    return { type: "seq", items };
  }

  private readQuantified(): Node {
    const node = this.readAtom();
    const quantifier = this.readQuantifier();
    if (!quantifier) return node;
    // A lazy or possessive quantifier matches the same strings as the greedy one; only which it prefers differs, and
    // a generator picking a count at random has no preference to express.
    if (this.peek() === "?" || this.peek() === "+") this.at++;
    return { type: "repeat", node, min: quantifier.min, max: quantifier.max };
  }

  private readQuantifier(): { min: number; max: number } | null {
    const character = this.peek();
    if (character === "?") return (this.at++, { min: 0, max: 1 });
    if (character === "*") return (this.at++, { min: 0, max: OPEN_REPEAT });
    if (character === "+") return (this.at++, { min: 1, max: OPEN_REPEAT });
    if (character !== "{") return null;

    const counted = /^\{(\d+)(,(\d*)?)?\}/.exec(this.source.slice(this.at));
    if (!counted) return null;
    this.at += counted[0].length;

    const min = Number(counted[1]);
    // `{3}` is exactly three, `{3,}` is three or more, `{3,7}` is a range; the open end is capped so a pattern
    // cannot ask for a megabyte of output.
    const max = counted[2] === undefined ? min : counted[3] ? Number(counted[3]) : min + OPEN_REPEAT;
    if (min > max || max > MAX_REPEAT) throw new Error("unreasonable quantifier");
    return { min, max };
  }

  private readAtom(): Node {
    const character = this.source[this.at];

    // An anchor says where a match sits rather than what is in it, and there is nothing around this string for it to
    // sit against, so it contributes no characters. The check at the end is what catches one that meant something.
    if (character === "^" || character === "$") return (this.at++, { type: "seq", items: [] });

    if (character === "(") {
      this.at++;
      // A capturing group and a non-capturing one generate alike; a lookaround and a named backreference do not.
      if (this.source.startsWith("?", this.at)) {
        const named = /^\?<[A-Za-z_$][\w$]*>/.exec(this.source.slice(this.at));
        if (this.source.startsWith("?:", this.at)) this.at += 2;
        else if (named) this.at += named[0].length;
        else throw new Error("lookaround");
      }
      const inner = this.readAlternation();
      if (this.source[this.at] !== ")") throw new Error("unclosed group");
      this.at++;
      return inner;
    }

    if (character === "[") return this.readClass();
    if (character === ".") return (this.at++, { type: "chars", set: ANY });
    if (character === ")" || character === "*" || character === "+" || character === "?") {
      throw new Error("nothing to repeat");
    }
    if (character === "\\") return this.readEscape();

    this.at++;
    return { type: "literal", text: character };
  }

  private readClass(): Node {
    this.at++;
    const negated = this.peek() === "^";
    if (negated) this.at++;

    let set = "";
    while (!this.done && this.peek() !== "]") {
      if (this.peek() === "\\") {
        const escape = this.readEscape();
        set += escape.type === "chars" ? escape.set : escape.text;
        continue;
      }

      const from = this.source[this.at++];
      // A hyphen is a range only between two characters; at either end of the class it is just a hyphen.
      if (this.peek() === "-" && this.source[this.at + 1] !== "]" && this.at + 1 < this.source.length) {
        this.at++;
        const to = this.source[this.at] === "\\" ? this.source[++this.at] : this.source[this.at];
        this.at++;
        for (let code = from.charCodeAt(0); code <= to.charCodeAt(0); code++) set += String.fromCharCode(code);
      } else {
        set += from;
      }
    }

    if (this.done) throw new Error("unclosed class");
    this.at++;
    const chosen = negated ? [...ANY].filter((character) => !set.includes(character)).join("") : set;
    if (!chosen) throw new Error("empty class");
    return { type: "chars", set: chosen };
  }

  private readEscape(): { type: "chars"; set: string } | { type: "literal"; text: string } {
    this.at++;
    const character = this.source[this.at++];
    if (character === undefined) throw new Error("trailing backslash");

    // A backreference has to equal what the group matched, which this generator has no way to promise.
    if (/[1-9kbB]/.test(character)) throw new Error("backreference or boundary");

    const named = CLASS_ESCAPES[character];
    if (named) return { type: "chars", set: named };
    return { type: "literal", text: LITERAL_ESCAPES[character] ?? character };
  }

  private peek(): string | undefined {
    return this.source[this.at];
  }
}

function render(rng: Rng, node: Node): string {
  switch (node.type) {
    case "literal":
      return node.text;
    case "chars":
      return node.set[rng.below(node.set.length)];
    case "seq":
      return node.items.map((item) => render(rng, item)).join("");
    case "alt":
      return render(rng, rng.pick(node.options));
    case "repeat": {
      const count = rng.between(node.min, Math.min(node.max, node.min + OPEN_REPEAT));
      return Array.from({ length: count }, () => render(rng, node.node)).join("");
    }
  }
}

const DIGITS = "0123456789";

const WORD = `${DIGITS}ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz_`;

// Printable ASCII without the space, which is what `.` and a negated class draw from: the whole of Unicode would
// hand back a value nobody can read and every downstream reader would report as a bug in the generator.
const ANY = Array.from({ length: 94 }, (_unused, index) => String.fromCharCode(33 + index)).join("");

const CLASS_ESCAPES: Record<string, string> = {
  d: DIGITS,
  D: [...ANY].filter((character) => !DIGITS.includes(character)).join(""),
  w: WORD,
  W: [...ANY].filter((character) => !WORD.includes(character)).join(""),
  // One space: a tab or a newline in generated data is a value nobody meant and every CSV reader chokes on.
  s: " ",
  S: ANY,
};

const LITERAL_ESCAPES: Record<string, string> = { n: "\n", r: "\r", t: "\t", f: "\f", v: "\v", "0": "\0" };

// How many times an open-ended `*`, `+` or `{n,}` is actually repeated: enough to look like a value, few enough that
// a nested pair of them cannot run away.
const OPEN_REPEAT = 4;

// A counted quantifier past this is a pattern asking for more output than anybody wants in a cell of a table.
const MAX_REPEAT = 512;
